import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import {
  getSOrdnerHeroDocuments,
  setSOrdnerCurrentHeroDocument,
  type SOrdnerHeroDocument,
} from "@/lib/sordnerOverview";
import { translateHeroDocumentType } from "@/projectOverview/heroDocumentDisplay";
import { OverviewCard } from "@/projectOverview/OverviewCard";

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);
  } catch {
    return String(value);
  }
}

function documentIdentity(doc: SOrdnerHeroDocument): number | null {
  const raw = doc.id ?? doc.customer_document_id;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function documentTitle(doc: SOrdnerHeroDocument): string {
  return String(doc.name || doc.nr || "Dokument").trim();
}

function documentHref(doc: SOrdnerHeroDocument): string | null {
  const href = doc.url || doc.download_url;
  return href ? String(href) : null;
}

function isOfferDocument(doc: SOrdnerHeroDocument): boolean {
  const blob = [doc.type, doc.document_type_name, doc.document_base_type]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return blob.includes("angebot") || blob.includes("offer");
}

export function SOrdnerHeroDocumentsSection({
  sordnerId,
  currentHeroDocumentId,
  onCurrentChanged,
}: {
  sordnerId: number;
  currentHeroDocumentId: number | null;
  onCurrentChanged: () => void;
}) {
  const [docs, setDocs] = useState<SOrdnerHeroDocument[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getSOrdnerHeroDocuments(sordnerId);
      setDocs(Array.isArray(list) ? list : []);
    } catch (err) {
      setDocs([]);
      setError(err instanceof Error ? err.message : "Dokumente konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [sordnerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const currentDoc = useMemo(() => {
    if (!currentHeroDocumentId || !docs?.length) return null;
    return docs.find((doc) => documentIdentity(doc) === currentHeroDocumentId) ?? null;
  }, [docs, currentHeroDocumentId]);

  async function handleSetCurrent(doc: SOrdnerHeroDocument | null) {
    const nextId = doc ? documentIdentity(doc) : null;
    if (doc && nextId == null) return;
    setSavingId(nextId);
    setError(null);
    try {
      await setSOrdnerCurrentHeroDocument(sordnerId, nextId);
      onCurrentChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Markierung konnte nicht gespeichert werden.");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <OverviewCard title="Hero-Dokumente">
      <p className="mb-3 text-sm text-label-secondary">
        Angebote und Rechnungen aus allen Projekten dieses S-Ordners. Markiere das
        aktuell gültige Angebot für nachfolgende Projekte.
      </p>

      {currentDoc ? (
        <div className="mb-4 rounded-ios border border-accent/35 bg-accent-soft/40 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-accent">
            Aktuelles Dokument
          </div>
          <div className="mt-1 font-medium text-label">{documentTitle(currentDoc)}</div>
          <div className="mt-1 text-xs text-label-secondary">
            {[
              currentDoc.auftragsnummer,
              translateHeroDocumentType(currentDoc.type || currentDoc.document_type_name),
              fmtDateTime(currentDoc.created_at),
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            disabled={savingId != null}
            onClick={() => void handleSetCurrent(null)}
          >
            Markierung entfernen
          </Button>
        </div>
      ) : null}

      {loading && docs === null ? (
        <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
          <Spinner />
          Dokumente werden geladen …
        </div>
      ) : null}

      {error ? (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {!loading && docs && docs.length === 0 ? (
        <p className="text-sm text-label-secondary">
          In den Projekten dieses S-Ordners sind noch keine Hero-Angebote oder -Rechnungen
          vorhanden.
        </p>
      ) : null}

      {docs && docs.length > 0 ? (
        <ul className="space-y-3">
          {docs.map((doc) => {
            const docId = documentIdentity(doc);
            const href = documentHref(doc);
            const isCurrent = docId != null && docId === currentHeroDocumentId;
            const isSaving = docId != null && savingId === docId;
            return (
              <li
                key={`${doc.auftrag_id}-${docId ?? documentTitle(doc)}`}
                className={clsx(
                  "rounded-ios border p-3",
                  isCurrent
                    ? "border-accent/40 bg-accent-soft/20"
                    : "border-separator bg-card-elevated",
                )}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {isCurrent ? (
                        <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-label-on-accent">
                          Aktuell
                        </span>
                      ) : null}
                      {doc.is_draft ? (
                        <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-[11px] font-semibold text-status-orange">
                          Entwurf
                        </span>
                      ) : null}
                      <span className="font-mono text-xs text-label-secondary">
                        {doc.auftragsnummer}
                      </span>
                    </div>
                    <div className="mt-1">
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-accent hover:underline"
                        >
                          {documentTitle(doc)}
                        </a>
                      ) : (
                        <span className="font-medium text-label">{documentTitle(doc)}</span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-label-secondary">
                      {[
                        doc.nr,
                        translateHeroDocumentType(doc.type || doc.document_type_name),
                        fmtDateTime(doc.created_at),
                        fmtMoney(doc.value ?? null),
                      ]
                        .filter((part) => part && part !== "—")
                        .join(" · ")}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {!isCurrent ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={isSaving}
                        disabled={savingId != null || docId == null}
                        onClick={() => void handleSetCurrent(doc)}
                      >
                        {isOfferDocument(doc) ? "Als aktuelles Angebot" : "Als aktuell markieren"}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </OverviewCard>
  );
}
